//
//  App.tsx
//  TrajeXY Signet Server
//
//  Copyright © 2026 TrajeXY. All rights reserved.
//
//  Created: September 23, 2026
//  Author: Bob Keathley
//
//  Tenant console root: routes, nav, and role-gated pages. Adopts an assumed
//  session token from the URL fragment (27 8a). Backstops 403 wrong_plane
//  from any route.
//
import { Component, useEffect, type ReactNode } from 'react'
import { getWrongPlane, setPasswordChangeRequiredHandler, setUnauthorizedHandler, subscribeWrongPlane } from '@shared/api/client'
import { auth, atLeast, isPlatformRole, useSession } from '@shared/auth'
import ChangePassword from '@shared/components/ChangePassword'
import Shell, { type NavItem } from '@shared/components/Shell'
import AssumedBanner from '@shared/components/AssumedBanner'
import WrongPlane from '@shared/components/WrongPlane'
import { useRoute } from '@shared/router'
import { useSyncExternalStore } from 'react'
import NotificationBell from './components/NotificationBell'
import AiAccounts from './pages/AiAccounts'
import Birth from './pages/Birth'
import Births from './pages/Births'
import Deeds from './pages/Deeds'
import Fleet from './pages/Fleet'
import Hierarchy from './pages/Hierarchy'
import Level from './pages/Level'
import Person from './pages/Person'
import Login from './pages/Login'
import Machines from './pages/Machines'
import Policy from './pages/Policy'
import Project from './pages/Project'
import Projects from './pages/Projects'
import Overview from './pages/Overview'
import Run from './pages/Run'
import Signal from './pages/Signal'
import Signals from './pages/Signals'
import Setup from './pages/Setup'
import Runs from './pages/Runs'
import Users from './pages/Users'
import Workstreams from './pages/Workstreams'
import '@shared/styles.css'

/** A render error in a page shows a message instead of a blank screen; navigating elsewhere clears it (keyed on the route). */
class PageBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (!this.state.error) return this.props.children
    return (
      <div className="projects">
        <p className="err" role="alert">This page hit an error and could not be shown: {this.state.error.message}</p>
        <p><a href="#/">‹ Overview</a> · <a href="#/hierarchy">Hierarchy</a></p>
      </div>
    )
  }
}

const NAV: NavItem[] = [
  { path: '/', label: 'Overview' },
  { path: '/fleet', label: 'Fleet' },
  { path: '/runs', label: 'Runs' },
  { path: '/workstreams', label: 'Workstreams' },
  { path: '/signals', label: 'Signals' },
]

export default function App() {
  const session = useSession()
  const route = useRoute()
  // Backstop for `403 wrong_plane` from any route (§1/§6g) -- on top of the plane check below,
  // which is the fast path and needs no round trip.
  const wrongPlaneFromApi = useSyncExternalStore(subscribeWrongPlane, getWrongPlane, getWrongPlane)
  useEffect(() => {
    setUnauthorizedHandler(() => auth.expire())   // a 401 anywhere = the session is gone
    setPasswordChangeRequiredHandler(() => auth.requirePasswordChange())   // SIG-66 §9: any 403 password_change_required
    const assumedToken = route.query.get('assumed_token')
    if (assumedToken) {
      // Landed here from the platform app's Assume page (27 §8a): adopt the minted tenant-plane
      // token as this tab's session, then drop it from the URL.
      const expiresAt = Number(route.query.get('assumed_expires')) || Date.now() / 1000 + 3600
      auth.adopt(assumedToken, expiresAt).then(() => history.replaceState(null, '', location.pathname + location.search + '#/'))
    } else {
      auth.refresh()                              // re-validate a restored session on load
    }
    return () => { setUnauthorizedHandler(null); setPasswordChangeRequiredHandler(null) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  if (route.parts[0] === 'setup') return <Setup code={route.query.get('code') ?? ''} />   // §6i owner setup link, works signed out
  if (!session) return <Login />
  if (session.password_change_required) return <ChangePassword session={session} forced />   // temp password: nothing else until it is changed
  if (isPlatformRole(session.user.role) || wrongPlaneFromApi) return <WrongPlane thisPlane="tenant" />
  const role = session.user.role
  const [head, id, sub] = route.parts
  let page
  if (head === 'fleet') page = <Fleet session={session} />
  else if (head === 'runs' && id) page = <Run session={session} sessionId={id} />
  else if (head === 'runs') page = <Runs session={session} />
  else if (head === 'signals' && id) page = <Signal session={session} signalId={id} />
  else if (head === 'signals') page = <Signals session={session} prefill={{ session: route.query.get('session') ?? undefined, agent: route.query.get('agent') ?? undefined }} />
  else if (head === 'workstreams') page = <Workstreams session={session} />
  else if (head === 'birth' && atLeast(role, 'team-admin')) page = <Birth session={session} />
  else if (head === 'births' && atLeast(role, 'team-admin')) page = <Births session={session} openId={route.query.get('id')} />
  else if (head === 'notebooks' && id && sub === 'deeds') page = <Deeds session={session} notebookId={id} />
  else if (head === 'machines' && atLeast(role, 'team-admin')) page = <Machines session={session} />
  else if (head === 'hierarchy' && id === 'corporate') page = <Level session={session} kind="corporate" refId={null} />
  else if (head === 'hierarchy' && id === 'teams' && sub) page = <Level session={session} kind="team" refId={sub} />
  else if (head === 'hierarchy' && id === 'projects' && sub) page = <Level session={session} kind="project" refId={sub} />
  else if (head === 'ai-accounts') page = <AiAccounts session={session} />
  else if (head === 'hierarchy' && id === 'users' && sub) page = <Person session={session} userId={sub} />
  else if (head === 'hierarchy') page = <Hierarchy session={session} />
  else if (head === 'projects' && id) page = <Project session={session} projectId={id} />
  else if (head === 'projects') page = <Projects session={session} />
  else if (head === 'policy') page = <Policy session={session} />
  else if (head === 'users' && atLeast(role, 'security-admin')) page = <Users session={session} />
  else page = <Overview session={session} />
  // Bob's ruling: team-admin can birth (was security-admin+). Machines is also team-admin+. Users
  // stays owner|security-admin (atLeast 'security-admin' covers both, since owner outranks it).
  const nav: NavItem[] = [
    ...NAV,
    { path: '/machines', label: 'Machines', show: atLeast(role, 'team-admin') },
    { path: '/birth', label: 'Birth', show: atLeast(role, 'team-admin') },
    { path: '/births', label: 'Births', show: atLeast(role, 'team-admin') },
    { path: '/projects', label: 'Projects', show: true },
    { path: '/hierarchy/users/me', label: 'My Notebook', show: true },
    { path: '/hierarchy', label: 'Hierarchy', show: true },
    { path: '/ai-accounts', label: 'AI accounts', show: true },
    { path: '/policy', label: 'Policy', show: true },
    { path: '/users', label: 'Users', show: atLeast(role, 'security-admin') },
  ]
  return (
    <Shell session={session} path={route.path} nav={nav} banner={<AssumedBanner session={session} />} actions={<NotificationBell session={session} />}>
      <PageBoundary key={route.path}>{page}</PageBoundary>
    </Shell>
  )
}
